import { useEffect, useRef, useState } from 'react';
import { ImageLightbox } from './ImageLightbox';

export type MediaThumbnailState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; url: string; previewKind: 'svg' | 'png' | 'jpeg' | 'webp' }
  | { kind: 'unpreviewable' }
  | { kind: 'expired' }
  | { kind: 'deleted' }
  | { kind: 'failed'; message: string };

export interface MediaThumbnailProps {
  state: MediaThumbnailState;
  label: string;
  lightboxTitle?: string;
  testId: string;
  lightboxTestId: string;
  onVisible?: () => void;
  onRetry?: () => void;
}

export function MediaThumbnail({
  state,
  label,
  lightboxTitle,
  testId,
  lightboxTestId,
  onVisible,
  onRetry,
}: MediaThumbnailProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const idle = state.kind === 'idle';

  useEffect(() => {
    if (!idle || !onVisible) return;
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        observer.disconnect();
        onVisible();
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [idle, onVisible]);

  return (
    <div
      ref={ref}
      data-testid={testId}
      data-thumbnail-state={state.kind}
      data-preview-kind={state.kind === 'ready' ? state.previewKind : undefined}
      className="flex h-12 w-16 shrink-0 items-center justify-center overflow-hidden rounded-md bg-sunken p-1"
    >
      {state.kind === 'ready' ? (
        <>
          <button
            type="button"
            data-testid={`${testId}-open`}
            className="block h-full w-full cursor-zoom-in rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={`Open ${lightboxTitle ?? label}`}
            onClick={() => setOpen(true)}
          >
            <img
              src={state.url}
              alt={label}
              loading="lazy"
              className="block h-full w-full rounded bg-transparent object-contain"
              style={{ filter: 'none' }}
            />
          </button>
          <ImageLightbox
            open={open}
            onOpenChange={setOpen}
            src={state.url}
            label={label}
            title={lightboxTitle}
            testId={lightboxTestId}
          />
        </>
      ) : null}
      {state.kind === 'unpreviewable' ? (
        <span className="text-micro text-muted-foreground">No preview</span>
      ) : null}
      {state.kind === 'expired' ? (
        <span data-testid={`${testId}-expired`} className="text-center text-micro text-muted-foreground">
          Image no longer available
        </span>
      ) : null}
      {state.kind === 'deleted' ? (
        <span data-testid={`${testId}-deleted`} className="text-center text-micro text-muted-foreground">
          Deleted
        </span>
      ) : null}
      {state.kind === 'failed' && onRetry ? (
        <button
          type="button"
          data-testid={`${testId}-retry`}
          className="text-center text-micro text-error-text hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          title={state.message}
          onClick={onRetry}
        >
          Failed · Retry
        </button>
      ) : null}
      {state.kind === 'failed' && !onRetry ? (
        <span className="text-micro text-error-text" role="alert" title={state.message}>
          Failed
        </span>
      ) : null}
    </div>
  );
}
